<div class="damaged-container">
  <div class="damaged-topbar">
    <div class="topbar-left">
      <h1>
        Damaged / Lost Equipment
        <small>Report and track damage or loss — send items to Maintenance when repairs are needed</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i> IMS / <span>Equipment Rental</span> / <span class="current">Damaged /
          Lost</span>
        <span class="separator">|</span>
        <span class="time"><i class="bi bi-clock"></i> {{ timestamp }}</span>
      </div>
    </div>
    <div class="topbar-right">
      <div class="search-box">
        <i class="bi bi-search"></i>
        <input type="text" placeholder="Search damaged/lost items..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)" />
      </div>
      <button class="btn btn-outline btn-sm" (click)="exportData()">
        <i class="bi bi-download"></i> Export
      </button>
      <button class="btn btn-danger" (click)="openReportModal()">
        <i class="bi bi-exclamation-triangle"></i> Report Damage / Loss
      </button>
    </div>
  </div>

  <div class="stats-row">
    <div class="stat-card help-card">
      <div class="stat-icon blue">
        <i class="bi bi-info-circle"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">How to send for repair</div>
        <div class="stat-value help-text">
          Open a <strong>Damaged</strong> row → <strong>Send to Maintenance</strong> → enter the technician →
          complete the job under <strong>Maintenance Jobs</strong>. When fixed, it leaves this list and stock is updated.
        </div>
      </div>
    </div>
  </div>

  <div class="stats-row">
    <div class="stat-card">
      <div class="stat-icon red">
        <i class="bi bi-exclamation-triangle"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Total Reports</div>
        <div class="stat-value">{{ damagedStats().total }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon amber">
        <i class="bi bi-tools"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Open / Review</div>
        <div class="stat-value">{{ damagedStats().open }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon blue">
        <i class="bi bi-wrench"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Damaged</div>
        <div class="stat-value">{{ damagedStats().damaged }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon dark-red">
        <i class="bi bi-x-circle"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Lost</div>
        <div class="stat-value">{{ damagedStats().lost }}</div>
      </div>
    </div>
  </div>

  <div class="filter-bar">
    <div class="filter-left">
      <div class="filter-group">
        <label><i class="bi bi-tag"></i> Type</label>
        <select [ngModel]="typeFilter()" (ngModelChange)="typeFilter.set($event)">
          <option value="">All Types</option>
          <option value="damaged">Damaged</option>
          <option value="lost">Lost</option>
        </select>
      </div>
      <div class="filter-group">
        <label><i class="bi bi-check-circle"></i> Status</label>
        <select [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)">
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="under-review">Under Review</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>
    </div>
    <div class="filter-right">
      <span class="filter-count">Showing {{ filteredRecords().length }} reports</span>
    </div>
  </div>

  <div class="table-wrap">
    <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Report ID</th>
            <th>Item</th>
            <th>Qty</th>
            <th>Type</th>
            <th>Details</th>
            <th>Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (record of filteredRecords(); track record.id) {
            <tr>
              <td><span class="report-id">{{ record.reportNumber }}</span></td>
              <td>
                <div class="item-info">
                  <span class="item-icon"><i class="bi" [class]="findAsset(record)?.icon ?? 'bi-box-seam'"></i></span>
                  <div>
                    <div class="item-name">{{ record.equipment }}</div>
                    <div class="item-id">
                      @if (record.itemCode) {
                        {{ record.itemCode }}
                      }
                      @if (record.assetNumber || findAsset(record)?.assetId) {
                        · {{ record.assetNumber || findAsset(record)?.assetId }}
                      }
                      @if (record.checkoutNumber) {
                        · {{ record.checkoutNumber }}
                      }
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div class="qty-cell">
                  @if ((record.quantityDamaged ?? 0) > 0) {
                    <div>Damaged: {{ record.quantityDamaged }}</div>
                  }
                  @if ((record.quantityMissing ?? 0) > 0) {
                    <div>Missing: {{ record.quantityMissing }}</div>
                  }
                  @if (!(record.quantityDamaged || record.quantityMissing)) {
                    <div>—</div>
                  }
                </div>
              </td>
              <td><span class="type-badge" [class]="record.damageType">{{ record.damageType | titlecase }}</span></td>
              <td>
                <div class="detail-cell">
                  @if (record.damageDescription) {
                    <div class="detail-primary">{{ record.damageDescription }}</div>
                  }
                  <div class="detail-meta">
                    @if (record.conditionIn) {
                      <span>{{ record.conditionIn }}</span>
                    }
                    @if (record.branchName) {
                      <span>{{ record.branchName }}</span>
                    }
                    @if (record.destination) {
                      <span>→ {{ record.destination }}</span>
                    }
                  </div>
                </div>
              </td>
              <td>{{ formatDate(record.reportedDate) }}</td>
              <td><span class="status-badge" [class]="record.status">{{ record.status }}</span></td>
              <td class="actions-cell">
                <div class="row-actions">
                  <button type="button" class="btn-action edit" title="Edit report" (click)="openEditModal(record)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button type="button" class="btn-action view" title="View report" (click)="openViewModal(record)">
                    <i class="bi bi-eye"></i>
                  </button>
                </div>
                @if (canSendToMaintenance(record)) {
                  <button class="btn btn-primary btn-sm" (click)="openRepairModal(record)">
                    <i class="bi bi-tools"></i> Send to Maintenance
                  </button>
                } @else if (record.status === 'under-review' && record.damageType === 'damaged') {
                  <span class="status-badge under-review">In Maintenance</span>
                } @else if (record.damageType === 'lost') {
                  <button class="btn btn-outline btn-sm" (click)="writeOffLost(record)">
                    <i class="bi bi-x-circle"></i> Write off
                  </button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  <div class="pagination-bar">
    <div class="pagination-info">
      Showing <span>1</span> to <span>{{ filteredRecords().length }}</span> of <span>{{ filteredRecords().length }}</span> reports
    </div>
  </div>
</div>

<!-- Report Modal -->
<div class="modal-overlay" [class.open]="showReportModal()" (click)="closeModalOnBackdrop($event)">
  <div class="modal modal-large">
    <div class="modal-header">
      <h2>
        <i class="bi" [class]="isEditing() ? 'bi-pencil' : 'bi-exclamation-triangle'"></i>
        {{ isEditing() ? 'Edit Report' : 'Report Damage / Loss' }}
      </h2>
      <button class="close-btn" (click)="closeReportModal()">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="modal-body">
      <div class="form-row">
        <div class="form-group">
          <label>Asset <span class="required">*</span></label>
          <select [ngModel]="formEquipment()" (ngModelChange)="formEquipment.set($event)">
            <option value="">Select Asset</option>
            @for (asset of allAssets(); track asset.id) {
              <option [value]="asset.id">{{ asset.assetId }} - {{ asset.name }}</option>
            }
          </select>
        </div>
        <div class="form-group">
          <label>Report Type <span class="required">*</span></label>
          <select [ngModel]="formDamageType()" (ngModelChange)="formDamageType.set($event)">
            <option value="damaged">Damaged</option>
            <option value="lost">Lost</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Report Date <span class="required">*</span></label>
          <input type="date" [ngModel]="formReportedDate()" (ngModelChange)="formReportedDate.set($event)" />
        </div>
        <div class="form-group">
          <label>Estimated Cost</label>
          <input type="number" min="0" [ngModel]="formEstimatedCost()" (ngModelChange)="formEstimatedCost.set(+$event)" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Status</label>
          <select [ngModel]="formStatus()" (ngModelChange)="formStatus.set($event)">
            <option value="open">Open</option>
            <option value="under-review">Under Review</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn btn-outline" (click)="closeReportModal()">Cancel</button>
      <button class="btn btn-danger" (click)="submitReport()">
        <i class="bi bi-check-lg"></i> {{ isEditing() ? 'Save Changes' : 'Submit Report' }}
      </button>
    </div>
  </div>
</div>

<!-- View Modal -->
@if (selectedRecord(); as record) {
  <div class="modal-overlay" [class.open]="showViewModal()" (click)="closeModalOnBackdrop($event)">
    <div class="modal">
      <div class="modal-header">
        <h2><i class="bi bi-eye"></i> Report Details</h2>
        <button class="close-btn" (click)="closeViewModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="detail-grid">
          <div class="detail-field">
            <label>Report ID</label>
            <span>{{ record.reportNumber }}</span>
          </div>
          <div class="detail-field">
            <label>Item</label>
            <span>{{ record.equipment }}</span>
          </div>
          <div class="detail-field">
            <label>Item code / Asset</label>
            <span>
              {{ record.itemCode || '—' }}
              @if (record.assetNumber || findAsset(record)?.assetId) {
                · {{ record.assetNumber || findAsset(record)?.assetId }}
              }
            </span>
          </div>
          <div class="detail-field">
            <label>Checkout</label>
            <span>{{ record.checkoutNumber || '—' }}</span>
          </div>
          <div class="detail-field">
            <label>Type</label>
            <span><span class="type-badge" [class]="record.damageType">{{ record.damageType | titlecase }}</span></span>
          </div>
          <div class="detail-field">
            <label>Status</label>
            <span><span class="status-badge" [class]="record.status">{{ record.status }}</span></span>
          </div>
          <div class="detail-field">
            <label>Qty damaged</label>
            <span>{{ record.quantityDamaged ?? 0 }}</span>
          </div>
          <div class="detail-field">
            <label>Qty missing / lost</label>
            <span>{{ record.quantityMissing ?? 0 }}</span>
          </div>
          <div class="detail-field">
            <label>Condition</label>
            <span>{{ record.conditionIn || '—' }}</span>
          </div>
          <div class="detail-field">
            <label>Branch</label>
            <span>{{ record.branchName || '—' }}</span>
          </div>
          <div class="detail-field">
            <label>Location</label>
            <span>{{ record.locationName || '—' }}</span>
          </div>
          <div class="detail-field">
            <label>Destination (when out)</label>
            <span>{{ record.destination || '—' }}</span>
          </div>
          <div class="detail-field">
            <label>Report Date</label>
            <span>{{ formatDate(record.reportedDate) }}</span>
          </div>
          <div class="detail-field">
            <label>Estimated Cost</label>
            <span>{{ formatCurrency(record.estimatedCost) }}</span>
          </div>
          <div class="detail-field full">
            <label>Description</label>
            <span>{{ record.damageDescription || '—' }}</span>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-outline" (click)="closeViewModal()">Close</button>
      </div>
    </div>
  </div>
}

<!-- Send to Maintenance Modal -->
@if (selectedRecord(); as record) {
  <div class="modal-overlay" [class.open]="showRepairModal()" (click)="closeModalOnBackdrop($event)">
    <div class="modal">
      <div class="modal-header">
        <h2><i class="bi bi-tools"></i> Send to Maintenance</h2>
        <button class="close-btn" (click)="closeRepairModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="detail-grid">
          <div class="detail-field">
            <label>Report</label>
            <span>{{ record.reportNumber }}</span>
          </div>
          <div class="detail-field">
            <label>Asset</label>
            <span>{{ findAsset(record)?.assetId }} - {{ record.equipment }}</span>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Responsible Person <span class="required">*</span></label>
            <input
              type="text"
              placeholder="Technician or staff member name"
              [ngModel]="formAssignedTo()"
              (ngModelChange)="formAssignedTo.set($event)"
            />
          </div>
          <div class="form-group">
            <label>Contact Phone</label>
            <input
              type="tel"
              placeholder="Optional contact number"
              [ngModel]="formAssignedPhone()"
              (ngModelChange)="formAssignedPhone.set($event)"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Repair Notes</label>
          <textarea
            rows="4"
            placeholder="Describe the damage and repair instructions"
            [ngModel]="formRepairNotes()"
            (ngModelChange)="formRepairNotes.set($event)"
          ></textarea>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-outline" (click)="closeRepairModal()" [disabled]="sendingToMaintenance()">
          Cancel
        </button>
        <button class="btn btn-primary" (click)="scheduleRepair()" [disabled]="sendingToMaintenance()">
          <i class="bi bi-tools"></i>
          {{ sendingToMaintenance() ? 'Sending...' : 'Send to Maintenance' }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Toast -->
<div class="toast-container" [class.show]="showToast()">
  <div
    class="toast"
    [class.success]="toastType() === 'success'"
    [class.error]="toastType() === 'error'"
    [class.info]="toastType() === 'info'"
  >
    <div class="toast-icon">
      @if (toastType() === 'error') {
        <i class="bi bi-x-circle-fill"></i>
      } @else if (toastType() === 'info') {
        <i class="bi bi-info-circle-fill"></i>
      } @else {
        <i class="bi bi-check-circle-fill"></i>
      }
    </div>
    <div class="toast-content">
      <div class="toast-title">
        {{ toastType() === 'error' ? 'Error' : toastType() === 'info' ? 'Notice' : 'Success!' }}
      </div>
      <div class="toast-message">{{ toastMessage() }}</div>
    </div>
    <button class="toast-close" (click)="closeToast()">
      <i class="bi bi-x-lg"></i>
    </button>
  </div>
</div>
